import styled, { keyframes } from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  min-height: calc(100vh - 64px);
  background: #f5f5f5;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #1f5454;
  }

  p {
    margin: 0;
    color: #666666;
    font-size: 16px;
  }
`;

export const ContentLayout = styled.div<{ $singleColumn: boolean }>`
  display: grid;
  grid-template-columns: ${({ $singleColumn }) =>
    $singleColumn ? "1fr" : "minmax(0, 1fr) minmax(320px, 360px)"};
  gap: 24px;
  align-items: start;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

export const SlotsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SlotsRow = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => Math.max(props.$columns ?? 1, 1)}, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  padding: 0 12px;
  overflow: hidden;
  min-height: 280px;
  transition: grid-template-columns 0.2s ease;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(${(props) => Math.max(props.$columns ?? 1, 1)}, minmax(0, 1fr));
    overflow-x: auto;
    padding-bottom: 12px;
    margin-bottom: 4px;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(58, 84, 84, 0.35);
      border-radius: 999px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
`;

export const SlotCardWrapper = styled.div<{ $isSelectable: boolean }>`
  display: flex;
  width: 100%;
  cursor: ${({ $isSelectable }) => ($isSelectable ? "pointer" : "default")};
`;

export const SlotCardSurface = styled.div<{
  $isCompleted: boolean;
  $isOccupied: boolean;
  $isSelected: boolean;
  $isSelectable: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 280px;
  border-radius: 12px;
  border: 2px dashed
    ${({ $isCompleted, $isOccupied, $isSelected }) =>
      $isCompleted
        ? "#52c41a"
        : $isOccupied
        ? $isSelected
          ? "#1890ff"
          : "#8c8c8c"
        : "#b5b5b5"};
  background-color:
    ${({ $isCompleted, $isOccupied, $isSelected }) =>
      $isCompleted
        ? "#52c41a"
        : $isOccupied
        ? $isSelected
          ? "#1890ff"
          : "#c2c2c2"
        : "#f5f5f5"};
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? "0 12px 24px rgba(15, 85, 85, 0.22)" : "none"};
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  ${({ $isSelectable }) =>
    $isSelectable
      ? `
    &:hover {
      transform: translateY(-4px);
    }
  `
      : ""}
`;

export const SlotCardBody = styled.div<{
  $isCompleted: boolean;
  $isOccupied: boolean;
}>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  color: ${({ $isCompleted, $isOccupied }) =>
    $isCompleted || $isOccupied ? "#ffffff" : "#999999"};
  word-break: break-word;
`;

export const SlotCardFooter = styled.div<{ $isCompleted: boolean }>`
  padding: 8px 12px;
  background-color: ${({ $isCompleted }) =>
    $isCompleted ? "#389e0d" : "#d9d9d9"};
  border-top: 1px solid ${({ $isCompleted }) =>
    $isCompleted ? "#2f8b0c" : "#c6c6c6"};
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $isCompleted }) => ($isCompleted ? "#ffffff" : "#333333")};
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

export const TasksColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const EmptyState = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  color: #666666;
  border: 2px dashed #d9d9d9;
`;

export const TasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const TaskCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);

  strong {
    font-size: 18px;
    color: #1f5454;
  }

  span {
    color: #888888;
    font-size: 14px;
  }
`;

export const ModalContent = styled.div<{ $hasSkuImages: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasSkuImages }) =>
    $hasSkuImages
      ? "minmax(280px, 1.1fr) minmax(340px, 1fr) minmax(260px, 0.8fr)"
      : "minmax(280px, 1fr) minmax(280px, 0.8fr)"};
  gap: 28px;
  align-items: stretch;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

export const ToteSection = styled.div`
  background: #f7f7f7;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SubSectionHeading = styled.h3`
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 700;
  color: #6b8b8b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

export const ContentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ContentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ContentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e1e1e1;
  box-shadow: 0 4px 12px rgba(31, 84, 84, 0.08);
`;

export const ContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 70%;
`;

export const ContentTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1f5454;
  word-break: break-word;
`;

export const ContentId = styled.span`
  font-size: 13px;
  color: #7a7a7a;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`;

export const ContentQuantity = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

export const ContentQuantityValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #1f5454;
`;

export const ContentQuantityLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #7a7a7a;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

export const ContentsPlaceholder = styled.div`
  font-size: 13px;
  color: #7a7a7a;
  padding: 8px 0;
`;

export const SkuSection = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 18px 32px rgba(31, 84, 84, 0.12);
`;

export const SkuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 0.8fr));
  gap: 18px;
`;

export const SkuCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  box-shadow: 0 12px 24px rgba(31, 84, 84, 0.14);
`;

export const SkuCardImage = styled.div<{ $imageUrl?: string }>`
  position: relative;
  height: 120px;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center / cover no-repeat`
      : "linear-gradient(180deg, #f2f2f2 0%, #dcdcdc 100%)"};
`;

export const SkuCardCodeBadge = styled.span`
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: #1f5454;
  letter-spacing: 0.6px;
  text-transform: uppercase;
`;

export const SkuCardPlaceholder = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 16px;
  font-weight: 700;
  color: #2a5a5a;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const SkuCardFooter = styled.div`
  background: #ffffff;
  padding: 14px 6px;
  border-top: 1px solid #d9d9d9;
  text-align: center;
`;

export const SkuCardFooterName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1f5454;
  display: block;
  word-break: break-word;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f5454;
`;

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  color: #444444;

  span:first-child {
    color: #777777;
  }

  span:last-child {
    font-weight: 600;
    color: #1f5454;
  }
`;

export const ActionSection = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 16px 40px rgba(31, 84, 84, 0.12);
`;

export const ActionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ActionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #595959;
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.8px;
`;

export const ActionDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #e0e0e0;
`;

export const ActionCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
`;

export const ActionPrompt = styled.div`
  flex: 1;
  background: #2f54eb;
  color: #ffffff;
  border-radius: 16px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  padding: 24px;
`;

const arrowPulse = keyframes`
  0% {
    transform: translateX(0);
    opacity: 0.55;
  }
  50% {
    transform: translateX(12px);
    opacity: 1;
  }
  100% {
    transform: translateX(0);
    opacity: 0.55;
  }
`;

export const ActionArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #ff4d4f;
  color: #ffffff;
  font-size: 28px;
  animation: ${arrowPulse} 1.4s infinite ease-in-out;

  svg {
    font-size: 28px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ActionDescription = styled.div`
  font-size: 15px;
  color: #4c4c4c;
  text-align: center;
`;

export const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

export const ActionFooter = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  text-align: center;
`;

export const PendingBadge = styled.span`
  background: #ff4d4f;
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;


